import { handleError } from 'lib/sentry'
import { Observable } from 'lib/shared/observer'
import { UnauthorizedError, UserOut, apiClient } from 'lib/apiClient'
import { QueryKey, queryClient } from 'lib/queryClient'

import { IDAVE_CLIENT_ID, IDAVE_URL } from './consts'
import { AuthPayloadParam, AuthRedirectParam, AuthType } from './types'

export class AuthService extends Observable<UserOut> {
  public permissions = new Set<string>()

  public user: UserOut | null = null

  public async signIn() {
    try {
      await AuthService.signInComplete()
      await this.fetchUser()
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        AuthService.signInRedirect(AuthType.Default)

        return
      }

      handleError(err)
    }
  }

  public async signOut() {
    try {
      await apiClient.signOut()
    } catch (err) {
      handleError(err)
    } finally {
      AuthService.signInRedirect(AuthType.Reauthenticate)
    }
  }

  public hasPermission(permission: string) {
    return this.permissions.has(permission)
  }

  public hasPermissions(list: string[]) {
    return list.every(p => this.permissions.has(p))
  }

  public async fetchUser() {
    const user = await queryClient.fetchQuery({
      queryKey: [QueryKey.whoami],
      queryFn: () => apiClient.whoami(),
    })

    this.user = user
    this.permissions = new Set(user.permissions)
    this.notify(this.user)

    return this.user
  }

  static async signInRedirect(authType: AuthType) {
    const authUrl = new URL(`${IDAVE_URL}/signin`)

    authUrl.searchParams.append(AuthRedirectParam.ResponseType, 'token id_token')
    authUrl.searchParams.append(AuthRedirectParam.ClientId, IDAVE_CLIENT_ID)
    authUrl.searchParams.append(AuthRedirectParam.AuthType, authType)
    authUrl.searchParams.append(
      AuthRedirectParam.RedirectUri,
      authType === AuthType.Default ? window.location.href : window.location.origin,
    )

    window.location.replace(authUrl.toString())
  }

  static async signInComplete() {
    const url = new URL(window.location.href)

    const id_token = url.searchParams.get(AuthPayloadParam.IdToken)
    const access_token = url.searchParams.get(AuthPayloadParam.AccessToken)
    const refresh_token = url.searchParams.get(AuthPayloadParam.RefreshToken)
    const client_id = url.searchParams.get(AuthPayloadParam.ClientId)

    if (!id_token || !access_token || !refresh_token || !client_id) {
      return
    }

    await apiClient.signInComplete({
      access_token,
      id_token,
      refresh_token,
      client_id,
    })
  }
}
